import { TZDateMini } from '@date-fns/tz';
import {
  endOfMonth,
  isAfter,
  isBefore,
  isSameMonth,
  startOfMonth,
} from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { formatDate } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useCalendarContext } from './useCalendarContext.js';
import { useFocus } from './useFocus.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type { ViewItem } from '../CalendarItem.js';
import type { UseCalendarItemProps } from '../types/shared-types.js';

/** Custom hook that retrieves properties from the current month. */
export function useMonth({ date, itemRef }: UseCalendarItemProps): ViewItem {
  const { state, dispatch } = useCalendarContext();
  const configuredTimezone = getTimezone();

  const intl = useIntl();

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select month',
      id: 'aRc3KoQgfpti2E9M',
      description: 'Aria label to add to each month button',
    });
    return `${formatMessage} ${formatDate(date, {
      month: 'long',
    })}`;
  }, [intl, date]);

  const isCurrent = useMemo(
    () => isSameMonth(date, state.today),
    [date, state.today],
  );

  const isFocused = useMemo(
    () => isSameMonth(date, state.activeDate),
    [date, state.activeDate],
  );

  const isSelected = useMemo(() => {
    if (isTimeframeV2(state.selectedDate, state.type)) {
      const { from, to } = state.selectedDate;
      const fromDate = from
        ? new TZDateMini(from.absoluteDate, configuredTimezone)
        : null;
      const toDate = to
        ? new TZDateMini(to.absoluteDate, configuredTimezone)
        : null;

      return !!(
        (fromDate && isSameMonth(fromDate, date)) ||
        (toDate && isSameMonth(toDate, date))
      );
    }

    const selectedDate = state.selectedDate
      ? startOfMonth(
          new TZDateMini(state.selectedDate.absoluteDate, configuredTimezone),
        )
      : null;

    if (!selectedDate) {
      return false;
    }

    return isSameMonth(selectedDate, date);
  }, [configuredTimezone, state.selectedDate, state.type, date]);

  const { tabIndex } = useFocus({ isFocused, itemRef });

  const isDisabled = useMemo(() => {
    if (!state.min && !state.max) {
      return false;
    }

    const startDay = startOfMonth(date);
    const endDay = endOfMonth(date);

    if (
      (state.min && isBefore(endDay, state.min)) ||
      (state.max && isAfter(startDay, state.max))
    ) {
      return true;
    }

    return false;
  }, [date, state.min, state.max]);

  /** Handle click on a month in the month view. */
  function onClick() {
    dispatch({
      type: 'activeDateChange',
      payload: { activeDate: date, changeToView: 'day' },
    });

    dispatch({ type: 'isCalendarBodyFocusedChange', payload: false });
  }

  return {
    ariaLabel,
    tabIndex,
    isCurrent,
    isSelected,
    isDisabled,
    onClick,
  };
}
